import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import OptionallyVisible from 'components/optionallyVisible';

import { AccountType, PwcCustomerType } from 'types/account';

import { LABELS } from '../keys';
import { CreatedAccount } from '../types';
import { BoldText, Heading, TextBlock, VerifyContainer } from './verify.styles';

interface CreateAccountModalVerifyProps {
  formValues: CreatedAccount;
  accountType: AccountType;
  pwcCustomerType: PwcCustomerType;
}

const TITLE_LABELS: Record<AccountType, string> = {
  [AccountType.Ramp]: t(LABELS.CREATE_MODAL.VERIFY_STEP.PRODUCT.RAMP),
  [AccountType.Pwc]: t(LABELS.CREATE_MODAL.VERIFY_STEP.PRODUCT.PWC),
  [AccountType.Pwc_Group]: t(LABELS.CREATE_MODAL.VERIFY_STEP.PRODUCT.GROUP_ACCOUNT),
  [AccountType.PSP]: t(LABELS.CREATE_MODAL.VERIFY_STEP.PRODUCT.PSP),
};

const PWC_CUSTOMER_TITLE_LABELS: Record<PwcCustomerType, string> = {
  [PwcCustomerType.Individual]: t(LABELS.CREATE_MODAL.PWC_CUSTOMER_OPTIONS.INDIVIDUAL),
  [PwcCustomerType.Business]: t(LABELS.CREATE_MODAL.PWC_CUSTOMER_OPTIONS.BUSINESS),
};

export const CreateAccountModalVerify = ({ formValues, accountType, pwcCustomerType }: CreateAccountModalVerifyProps) => {
  const { t } = useTranslation();

  return (
    <VerifyContainer>
      <Heading>{t(LABELS.CREATE_MODAL.VERIFY_STEP.TITLE)}</Heading>
      <TextBlock>
        <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.PRODUCT.TITLE)}</div>
        <BoldText>{TITLE_LABELS[accountType]}</BoldText>
      </TextBlock>

      <TextBlock>
        <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.ACCOUNT_NAME_LABEL)}</div>
        <BoldText>{formValues.accountName}</BoldText>
      </TextBlock>

      <OptionallyVisible visible={accountType === AccountType.Pwc && pwcCustomerType === PwcCustomerType.Business}>
        <TextBlock>
          <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.MAIN_FIAT_CURRENCY)}</div>
          <BoldText>{t(LABELS.CREATE_MODAL.CURRENCY_LABELS[formValues.mainFiatCurrency])}</BoldText>
        </TextBlock>
      </OptionallyVisible>

      <OptionallyVisible visible={accountType === AccountType.Pwc}>
        <TextBlock>
          <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.GROUP_ACCOUNT_ID_LABEL)}</div>
          <BoldText>{formValues.groupAccountId ? formValues.groupAccountId : t(LABELS.CREATE_MODAL.VERIFY_STEP.NO_VALUE)}</BoldText>
        </TextBlock>

        <TextBlock>
          <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.MERCHANT_ID_LABEL)}</div>
          <BoldText>{formValues.shift4MerchantId ? formValues.shift4MerchantId : t(LABELS.CREATE_MODAL.VERIFY_STEP.NO_VALUE)}</BoldText>
        </TextBlock>
      </OptionallyVisible>

      <OptionallyVisible visible={accountType === AccountType.Pwc_Group}>
        <TextBlock>
          <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.PARENT_ACCOUNT_ID_LABEL)}</div>
          <BoldText>{formValues.parentAccountName || formValues.parentAccountUuid || t(LABELS.CREATE_MODAL.VERIFY_STEP.NO_VALUE)}</BoldText>
        </TextBlock>
      </OptionallyVisible>

      <TextBlock>
        <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.ACCOUNT_OWNER_LABEL)}</div>
        <BoldText>{formValues.username}</BoldText>
        <BoldText>
          {formValues.firstName} {formValues.lastName}
        </BoldText>
      </TextBlock>

      <OptionallyVisible visible={accountType === AccountType.Pwc}>
        <TextBlock>
          <div>{t(LABELS.CREATE_MODAL.VERIFY_STEP.PWC_CUSTOMER_TYPE)}</div>
          <BoldText>{PWC_CUSTOMER_TITLE_LABELS[pwcCustomerType]}</BoldText>
        </TextBlock>
      </OptionallyVisible>
    </VerifyContainer>
  );
};
